import { Eye, EyeOff } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { getAutoDirection } from "@/lib/direction";
import { cn, getTestIdWithPrefix } from "@/lib/utils";

interface IInputStyledProps extends Omit<
  React.ComponentProps<"input">,
  "type"
> {
  wrapperClassName?: string;
  "data-testid"?: string;
}

const PasswordInput = React.forwardRef<HTMLInputElement, IInputStyledProps>(
  (
    { className, wrapperClassName, "data-testid": dataTestId, ...props },
    ref,
  ) => {
    const { t } = useTranslation("common");
    const [isVisible, setIsVisible] = React.useState(false);

    return (
      <div className={cn("relative", wrapperClassName)}>
        <input
          dir={getAutoDirection({ isDisabled: !props.value })}
          data-testid={dataTestId}
          type={isVisible ? "text" : "password"}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-input-background px-3 py-2 pe-10 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className,
          )}
          ref={ref}
          {...props}
        />
        <button
          type="button"
          aria-label={
            isVisible ? t("password_input.hide") : t("password_input.show")
          }
          aria-pressed={isVisible}
          className="absolute ltr:right-3 top-1/2 -translate-y-1/2 rounded-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rtl:left-3"
          data-testid={getTestIdWithPrefix("toggle-button", dataTestId)}
          onMouseDown={(e) => {
            // NOTE: Keep caret/focus in the input on pointer interaction; the
            // toggle itself runs from onClick so keyboard activation works too.
            e.preventDefault();
          }}
          onClick={() => {
            setIsVisible((prev) => !prev);
          }}
        >
          {isVisible ? (
            <Eye className="h-5 w-auto text-muted-foreground" />
          ) : (
            <EyeOff className="h-5 w-auto text-muted-foreground" />
          )}
        </button>
      </div>
    );
  },
);

export { PasswordInput };
